import { useEffect, useState } from "react"
import { listJobs } from "@/services/jobs"
import { cachedRead } from "@/utils/readCache"
import type { Facilitator } from "@/types"

export function peopleSummary(people: readonly Facilitator[]): string {
  if (people.length === 0) return "Just you"
  const first = people[0].displayName || people[0].email
  const rest = people.length - 1
  if (rest === 0) return first
  return `${first} + ${rest} ${rest === 1 ? "other" : "others"}`
}

/**
 * Cards drafted (every job's shortlist) vs. kept (filed as a Must have),
 * summed across every job the session ran — the same two numbers the debrief
 * calls "opportunities" and "build ready". Only fetched once a session is
 * complete: a live or paused one has no finished shortlist to report yet.
 *
 * Held per session by `cachedRead`, so a card remounted by a filter or a
 * re-render does not re-read the jobs it has already counted. A complete
 * session's jobs are finished documents, so there is nothing for the window to
 * be stale about.
 */
export function useJobCardStats(
  sessionId: string,
  done: boolean
): { drafted: number; kept: number } | null {
  const [stats, setStats] = useState<{ drafted: number; kept: number } | null>(null)
  useEffect(() => {
    // A session only ever becomes complete, never un-becomes it, so there is
    // nothing to reset here — the initial `null` already covers "not done yet".
    if (!done) return
    let live = true
    cachedRead(`jobs:${sessionId}`, () => listJobs(sessionId)).then((jobs) => {
      if (!live) return
      const drafted = jobs.reduce((total, job) => total + job.cardIds.length, 0)
      const kept = jobs.reduce(
        (total, job) =>
          total + job.cardIds.filter((id) => job.placements[id]?.bucket === "must").length,
        0
      )
      setStats({ drafted, kept })
    }, noop)
    return () => {
      live = false
    }
  }, [sessionId, done])
  return stats
}

function noop() {}
